import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBusinessTime } from '@fortawesome/free-solid-svg-icons';
import { formatMessage } from 'i18n/CurrentLocale';
import { ReduxState } from 'types';
import * as companyActions from 'features/company/companyActions';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import { ModalEnum } from 'myParkdUtils/enums';
import { getEditableCompanyDetails } from 'screens/modals/EditCompanyWorkingHoursModal';
import { useEffect, useState } from 'react';
import { usePrevious } from 'myParkdUtils/customHooks';
import { FormSource } from 'features/company/companyDetails';


export const CompanyWorkingHoursForm = (props: Props) => {
    const [formSubmitted, setFormSubmitted] = useState(false);
    const { workingHoursStart, workingHoursEnd } = props.companyDetails?.companyDetails;
    const hasWorkingHours = (!!workingHoursStart && !!workingHoursEnd);

    const companyUpdateStatus = props.companyDetails?.companyUpdateStatus;
    const prevCompanyUpdateStatus = usePrevious(companyUpdateStatus);

    useEffect(() => {
        if (prevCompanyUpdateStatus && prevCompanyUpdateStatus.loading &&
            (companyUpdateStatus.formSource === FormSource.UPDATE_WORKING_HOURS || companyUpdateStatus.formSource === FormSource.REMOVE_WORKING_HOURS) &&
            (companyUpdateStatus.succeeded || companyUpdateStatus.error)
        ) {
            setFormSubmitted(true);
        } else {
            setFormSubmitted(false);
        }
    }, [companyUpdateStatus]);


    const removeWorkingHours = () => {
        props.updateModal(ModalEnum.CONFIRMATION_DIALOG, {
            modalTitle: <span><FontAwesomeIcon icon={faBusinessTime} /> {formatMessage({ id: 'SETTINGS_REMOVE_WH_CONFIRM_DIALOG_TITLE' })}</span>,
            modalText: formatMessage({ id: 'SETTINGS_REMOVE_WH_CONFIRM_DIALOG_TEXT' }),
            confirmButtonText: formatMessage({ id: 'SETTINGS_REMOVE_WH_CONFIRM_DIALOG_CONFIRM_BUTTON' }),
            loadingMessage: <FormattedMessage id="SETTINGS_REMOVE_WH_CONFIRM_DIALOG_LOADING_MESSAGE" />,
            successMessage: <FormattedMessage id="SETTINGS_REMOVE_WH_CONFIRM_DIALOG_SUCCESS_MESSAGE" />,
            actionOnConfirm: async () => {
                props.updateCompanyDetails({
                    ...getEditableCompanyDetails(props.companyDetails.companyDetails),
                    workingHoursStart: null,
                    workingHoursEnd: null,
                }, 'REMOVE_WORKING_HOURS');
            }
        });
    }

    return (
        <div id="companyWorkingHoursForm" className="radius-rectangle settings-box">
            <div className="settings-box-header">
                <h3 className="bold-title">
                    <FontAwesomeIcon icon={faBusinessTime} className="icon-before" />
                    <FormattedMessage id="SETTINGS_WH_TITLE" />
                </h3>
            </div>
            <div className="settings-box-content">
                <div style={{ marginBottom: '1em' }}>
                    <p>
                        <FormattedMessage id='SETTINGS_WH_DESCRIPTION' />
                    </p>
                </div>

                {!!hasWorkingHours && (
                    <div style={{ marginBottom: '20px' }}>
                        <label><FormattedMessage id='SETTINGS_WH_CURRENT_WORKING_HOURS' /></label>
                        <div>
                            <div className="text-field" style={{ background: '#fff', display: 'inline-block' }}
                                onClick={() => props.updateModal(ModalEnum.EDIT_WORKING_HOURS_COMPANY)}
                            >
                                {workingHoursStart.slice(0, 5)} - {workingHoursEnd.slice(0, 5)}
                            </div>
                        </div>
                    </div>
                )}

                {formSubmitted && (
                    <>
                        {companyUpdateStatus.succeeded && companyUpdateStatus.formSource === FormSource.UPDATE_WORKING_HOURS && (
                            <div className="success-box" data-testid="working-hours-update-success-message">
                                <FormattedMessage id="SETTINGS_WH_UPDATE_STATUS_SUCCESS" />
                            </div>
                        )}
                        {companyUpdateStatus.error && (
                            <div className="error-box">
                                <FormattedMessage id="PARKD_API_ERROR_FATAL_ERROR" />
                            </div>
                        )}
                    </>)
                }

                {hasWorkingHours ?
                    <>
                        <button className='btn-blue' onClick={() => props.updateModal(ModalEnum.EDIT_WORKING_HOURS_COMPANY)}>
                            <FormattedMessage id='SETTINGS_WH_BTN_EDIT' />
                        </button>
                        <button className='btn-red' onClick={() => removeWorkingHours()} style={{ marginLeft: '10px' }}>
                            <FormattedMessage id='SETTINGS_WH_BTN_REMOVE' />
                        </button>
                    </>
                    :
                    <button className='btn-blue' onClick={() => props.updateModal(ModalEnum.EDIT_WORKING_HOURS_COMPANY)}>
                        <FormattedMessage id='SETTINGS_WH_BTN_ADD' />
                    </button>
                }
            </div>
        </div>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

/* istanbul ignore next */
function mapStateToProps(state: ReduxState) {
    return {
        companyDetails: state.companyDetails,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        updateCompanyDetails: companyActions.updateCompanyDetails,
        updateModal: modalWindowActions.updateModalWindow,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyWorkingHoursForm);